import { isEmpty } from "psims/lib/empty";
import { recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { MsoStockholdingStorage } from "../types";

const CHAR_LIMIT = 200;

const ENTITY_NAME_REGEX = /^[a-zA-Z0-9\-,'() ]+$/;

// Allows integers and decimals with up to 2 decimal places
const DECIMAL_2DP_REGEX = /^\d+(\.\d{1,2})?$/;

function hasInvalidCharacters(value: string | undefined | null): boolean {
  if (!value) return false;
  return !ENTITY_NAME_REGEX.test(value);
}

function hasInvalidDecimal(value: number | string | undefined | null): boolean {
  if (value == null || value === '') return false;
  return !DECIMAL_2DP_REGEX.test(String(value));
}

export type StorageValidationField =
  | 'terminalName'
  | 'terminalLocation'
  | 'terminalCoOwned'
  | 'terminalOwners'
  | 'terminalOperator'
  | 'physicalStorageCapacity'
  | 'capacityReservedForCompany'
  | 'capacityReservedForYou'
  | 'receiveProducts'
  | 'distributeProducts'
  | 'nameOfOtherCompany'
  | 'typeOfArrangementTxt'
  | 'delete';

export type StorageValidationResult = (
  {
    key: Exclude<StorageValidationField, 'delete'>;
  } & (
    | { code: 'invalid_required' }
    | { code: 'character_limit'; limit: number }
    | { code: 'invalid_characters' }
    | { code: 'invalid_decimal'; decimalPlaces: number }
  )
) | {
  key: 'delete';
  code: 'inactive_product';
};

export function validateStorage(
  storage: MsoStockholdingStorage,
  productStatus: 'active' | 'inactive'
): Array<StorageValidationResult> {
  const messages: Array<StorageValidationResult> = [];

  if (productStatus === 'inactive') {
    if (recordActionFromEnum(storage.recordAction) === 'Delete' || storage.id == null) {
      return messages;
    }
    messages.push({ key: 'delete', code: 'inactive_product' });
    return messages;
  }

  if (recordActionFromEnum(storage.recordAction) === 'Delete') {
    return messages;
  }

  // ── Terminal Name ─────────────────────────────────────────────────────────
  if (isEmpty(storage.terminalName)) {
    messages.push({ key: 'terminalName', code: 'invalid_required' });
  } else if ((storage.terminalName?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'terminalName', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.terminalName)) {
    messages.push({ key: 'terminalName', code: 'invalid_characters' });
  }

  // ── Terminal Location ─────────────────────────────────────────────────────
  if (isEmpty(storage.terminalLocation)) {
    messages.push({ key: 'terminalLocation', code: 'invalid_required' });
  } else if ((storage.terminalLocation?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'terminalLocation', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.terminalLocation)) {
    messages.push({ key: 'terminalLocation', code: 'invalid_characters' });
  }

  // ── Terminal Co-owned ─────────────────────────────────────────────────────
  if (isEmpty(storage.terminalCoOwned)) {
    messages.push({ key: 'terminalCoOwned', code: 'invalid_required' });
  }

  // ── Terminal Owners ───────────────────────────────────────────────────────
  if (isEmpty(storage.terminalOwners)) {
    messages.push({ key: 'terminalOwners', code: 'invalid_required' });
  } else if ((storage.terminalOwners?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'terminalOwners', code: 'character_limit', limit: CHAR_LIMIT });
  }

  // ── Terminal Operator ─────────────────────────────────────────────────────
  if (isEmpty(storage.terminalOperator)) {
    messages.push({ key: 'terminalOperator', code: 'invalid_required' });
  } else if ((storage.terminalOperator?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'terminalOperator', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.terminalOperator)) {
    messages.push({ key: 'terminalOperator', code: 'invalid_characters' });
  }

  // ── Physical Storage Capacity — required, decimal 2dp ────────────────────
  if (isEmpty(storage.physicalStorageCapacity)) {
    messages.push({ key: 'physicalStorageCapacity', code: 'invalid_required' });
  } else if (hasInvalidDecimal(storage.physicalStorageCapacity)) {
    messages.push({ key: 'physicalStorageCapacity', code: 'invalid_decimal', decimalPlaces: 2 });
  }

  // ── Receive Products ──────────────────────────────────────────────────────
  if (isEmpty(storage.receiveProducts)) {
    messages.push({ key: 'receiveProducts', code: 'invalid_required' });
  } else if ((storage.receiveProducts?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'receiveProducts', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.receiveProducts)) {
    messages.push({ key: 'receiveProducts', code: 'invalid_characters' });
  }

  // ── Distribute Products ───────────────────────────────────────────────────
  if (isEmpty(storage.distributeProducts)) {
    messages.push({ key: 'distributeProducts', code: 'invalid_required' });
  } else if ((storage.distributeProducts?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'distributeProducts', code: 'character_limit', limit: CHAR_LIMIT });
  } else if (hasInvalidCharacters(storage.distributeProducts)) {
    messages.push({ key: 'distributeProducts', code: 'invalid_characters' });
  }

  // ── Name of Other Company — optional, char limit only ────────────────────
if (!isEmpty(storage.nameOfOtherCompany)) {
  if ((storage.nameOfOtherCompany?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'nameOfOtherCompany', code: 'character_limit', limit: CHAR_LIMIT });
  }
}

// ── Type of Arrangement — optional, char limit only ───────────────────────
if (!isEmpty(storage.typeOfArrangementTxt)) {
  if ((storage.typeOfArrangementTxt?.length || 0) > CHAR_LIMIT) {
    messages.push({ key: 'typeOfArrangementTxt', code: 'character_limit', limit: CHAR_LIMIT });
  }
}

  // ── Capacity Reserved for Company — optional, decimal 2dp ────────────────
  if (!isEmpty(storage.capacityReservedForCompany)) {
    if (hasInvalidDecimal(storage.capacityReservedForCompany)) {
      messages.push({ key: 'capacityReservedForCompany', code: 'invalid_decimal', decimalPlaces: 2 });
    }
  }

  // ── Capacity Reserved for You — optional, decimal 2dp ────────────────────
  if (!isEmpty(storage.capacityReservedForYou)) {
    if (hasInvalidDecimal(storage.capacityReservedForYou)) {
      messages.push({ key: 'capacityReservedForYou', code: 'invalid_decimal', decimalPlaces: 2 });
    }
  }

  return messages;
}